import { useTranslations } from 'next-intl';
import type { ImageResolution } from '@/app/types';
import { getImageValidationErrorMessage, type ImageValidationReason, validateImageFile } from '@/app/utils/client';

interface ImageValidatorOptions {
  maxFileSize: number;
  validateResolution?: (imageResolution: ImageResolution) => {
    errorMessage: string;
    errorReason: string;
  } | null;
}

export type ImageValidatorResult =
  | {
      valid: false;
      errorMessage?: string;
      errorReason?: string;
    }
  | {
      valid: true;
      imageURL: string;
      imageResolution: ImageResolution;
    };

/**
 * 图片验证 Hook
 *
 * 内部委托给 `validateImageFile` 纯函数 + `getImageValidationErrorMessage` 文案工具，
 * 返回结构与历史版本保持一致，工具侧调用方无须感知重构。
 */
export const useImageValidator = ({ maxFileSize, validateResolution }: ImageValidatorOptions) => {
  const t = useTranslations();

  /**
   * 验证图片类型、大小和尺寸
   * @returns 成功返回 { valid: true, imageURL, imageResolution }，失败返回 { valid: false, errorReason, errorMessage }
   */
  const validateImage = async (file: File): Promise<ImageValidatorResult> => {
    // 把工具侧的 errorMessage 单独缓存，避免 `validateResolution` 钩子的自定义文案在
    // 翻译层被默认 i18n key 覆盖。
    let resolutionFailure: { errorMessage: string; errorReason: string } | null = null;

    const result = await validateImageFile(file, {
      maxFileSize,
      validateResolution: (imageResolution) => {
        const failure = validateResolution?.(imageResolution);
        if (!failure) return null;
        resolutionFailure = failure;
        return failure.errorReason as ImageValidationReason;
      },
    });

    if (result.valid) {
      return result;
    }

    if (resolutionFailure) {
      return {
        valid: false,
        errorReason: (resolutionFailure as { errorMessage: string; errorReason: string }).errorReason,
        errorMessage: (resolutionFailure as { errorMessage: string; errorReason: string }).errorMessage,
      };
    }

    return {
      valid: false,
      errorReason: result.reason,
      errorMessage: getImageValidationErrorMessage(result.reason, { t, maxFileSize }),
    };
  };

  return { validateImage };
};
